import { useRef } from 'react';
import { useNavigateWithParams } from '@hooks/useNavigateWithParams';
import toast from 'react-hot-toast';

import {
    HandleMissingTags,
    ImportJobRepresentation,
    ImportJobSource,
    ImportJobStatus,
    ColumnMetaDataContext,
} from '@common/api';
import { useI18nContext } from '../../i18n/i18n-react';
import { pagePath } from '@pages/pagePaths';

// Components
import { Button } from '@components/button';
import { useModal } from '@components/modal/ModalProvider';
import { Confirmation } from '@components/confirmation/Confirmation';
import ImportConfigureModal from './ImportConfigureModal';
import ImportActionsMenu from './ImportActionsMenu';
import * as FarseerIcons from '@components/icons/FarseerIcons';
import { Delete } from '@components/icons/FarseerIcons';
import { Tooltip } from '@components/tooltip/Tooltip';
import { ContextMenu } from '@components/context-menu/ContextMenu';

// Hooks
import { accountTagToEntityKey } from '@data/entity/legacy';
import { useMissingTags } from '@data/hooks/missingTags';
import {
    useCommitImportJob,
    useImportMissingDimensions,
    useRevertImportJob,
    useUpdateImportJob,
    useImportJobRows,
} from '@data/importJob/importJobActions';
import { useGlobalLoadingContext } from '@features/loading/globalLoadingContext';
import { useEntityContext } from '@providers/EntityProvider';

import { downloadFarseerFile } from './util';
import { ImportJobMissingTags } from './types';
import { DeleteImportJobModal } from './DeleteImportJobModal';

interface ImportJobActionsProps {
    importJob: Readonly<ImportJobRepresentation>;
}

const EMPTY_ROWS: never[] = [];

export const ImportJobActions = ({ importJob }: ImportJobActionsProps) => {
    const { LL } = useI18nContext();
    const navigate = useNavigateWithParams();
    const { getEntity } = useEntityContext();
    const { showModal, hideModal } = useModal();
    const importJobRows = useImportJobRows(importJob.id).data?.rows ?? EMPTY_ROWS;

    const missingTags: ImportJobMissingTags | null = useMissingTags(importJob.metadata.columnMetas, importJobRows);

    const updateImportJob = useUpdateImportJob();
    const commitImportJob = useCommitImportJob();
    const revertImportJob = useRevertImportJob();
    const importMissingDimensions = useImportMissingDimensions();
    const { globalLoadingWrapped } = useGlobalLoadingContext();

    const columnMetas = importJob.metadata.columnMetas;

    const importJobEditable =
        importJob.status === ImportJobStatus.CREATED || importJob.status === ImportJobStatus.REVERTED;

    const hasMissingTags = missingTags !== null && Object.keys(missingTags).length > 0;
    const hasAssignedTableColumn = columnMetas.some(
        (columnMeta) => columnMeta.dataContext === ColumnMetaDataContext.IDENTIFIER && columnMeta.tableTagId
    );
    const hasAssignedVariableColumn = columnMetas.some(
        (columnMeta) =>
            (columnMeta.dataContext === ColumnMetaDataContext.VALUES && columnMeta.applyTagsIds?.length) ||
            (columnMeta.dataContext === ColumnMetaDataContext.TIMEPOINT &&
                columnMeta.timePointId !== undefined &&
                columnMeta.applyTagsIds?.length)
    );
    const hasDisallowedMissingTags =
        importJob.metadata.handleMissingTags === HandleMissingTags.DISALLOW && hasMissingTags;

    const importDimensionsDisabled = !hasAssignedTableColumn || !hasMissingTags || !importJobEditable;
    const importDimensionsReason = !hasAssignedTableColumn ? LL.NO_COLUMN_SET_AS_TABLE() : null;

    const importValuesDisabled = hasDisallowedMissingTags || !hasAssignedVariableColumn || !importJobEditable;
    const importValuesReason = hasDisallowedMissingTags
        ? LL.MISSING_MEMBERS()
        : !hasAssignedVariableColumn
        ? LL.NO_COLUMN_SET_AS_VALUE()
        : null;

    const goToPreviousImports = () => {
        navigate(`${pagePath.Import}`);
    };

    const commitImportJobHandler = async () => {
        const result = await globalLoadingWrapped(() => commitImportJob.mutateAsync(importJob.id));
        if (result !== undefined) {
            const accountTagInfo: string[] = [];
            for (const importCellInfo of result.importedCells) {
                const accountTag = getEntity(accountTagToEntityKey(importCellInfo.accountTagId));
                if (accountTag !== undefined) {
                    accountTagInfo.push(
                        LL.TAG_NAME_CELLS({ tagName: accountTag.meta.name, cellNumber: importCellInfo.count })
                    );
                } else {
                    accountTagInfo.push(LL.UNKNOWN_VARIABLE_CELLS({ cellNumber: importCellInfo.count }));
                }
            }
            toast.success(LL.SUCCESS_IMPORTED_CELLS({ accountTags: accountTagInfo.join('\n') }));
        }
    };

    const revertImportJobHandler = async () => {
        await globalLoadingWrapped(async () => {
            await revertImportJob.mutateAsync(importJob.id);
        });
    };

    const archiveImportJobHandler = async () => {
        await globalLoadingWrapped(async () => {
            await updateImportJob.mutateAsync({ id: importJob.id, status: ImportJobStatus.ARCHIVED });
        });
        goToPreviousImports();
    };

    const deleteImportJobHandler = () => {
        showModal(<DeleteImportJobModal importJob={importJob} onDeleted={goToPreviousImports} />);
    };

    const replaceAllPreviousDimensions = () => {
        showModal(
            <Confirmation
                title={LL.MISSING_MEMBERS_REPLACE_ALL()}
                question={LL.MISSING_MEMBERS_REPLACE_ALL_CONFIRMATION()}
                onConfirm={async () => {
                    globalLoadingWrapped(async () => {
                        await importMissingDimensions.mutateAsync({
                            id: importJob.id,
                            replaceAll: true,
                        });
                    });
                    hideModal();
                }}
                onCancel={() => hideModal()}
            />
        );
    };

    const replaceImportJob = () => {
        navigate(`${pagePath.Import}/replace/${importJob.id}`);
    };

    const showConfigureModal = () => {
        showModal(
            <ImportConfigureModal
                importJobId={importJob.id}
                importJobMetadata={importJob.metadata}
                closeModal={() => hideModal()}
            />
        );
    };

    const undoJobButton = <Button $type="primary" $color="error" title={LL.UNDO()} onClick={revertImportJobHandler} />;

    const handleImportMissingDimensions = async () => {
        await globalLoadingWrapped(async () => {
            await importMissingDimensions.mutateAsync({ id: importJob.id });
        });
    };

    const importDimensionsRef = useRef<HTMLButtonElement>(null);
    const importValuesRef = useRef<HTMLButtonElement>(null);

    const activeImportJobButtons = (
        <>
            <Button $type="quaternary" title={LL.CONFIGURE()} onClick={showConfigureModal} />
            <Button
                $type="secondary"
                $color="primary"
                disabled={importDimensionsDisabled}
                title={LL.IMPORT_MISSING_MEMBERS()}
                onClick={handleImportMissingDimensions}
                ref={importDimensionsRef}
            />
            <Button
                $type="secondary"
                $color="primary"
                disabled={importValuesDisabled}
                title={LL.IMPORT_VALUES()}
                onClick={commitImportJobHandler}
                ref={importValuesRef}
            />
            {importDimensionsReason != null ? (
                <Tooltip
                    label={importDimensionsReason}
                    anchorElementRef={importDimensionsRef}
                    allowedPlacements={['bottom']}
                />
            ) : null}
            {importValuesReason != null ? (
                <Tooltip
                    label={importValuesReason}
                    anchorElementRef={importValuesRef}
                    allowedPlacements={['bottom-end']}
                />
            ) : null}
        </>
    );

    return (
        <>
            <ImportActionsMenu>
                <ContextMenu.Item
                    item={{
                        label: LL.ARCHIVE(),
                        icon: FarseerIcons.Archive,
                        disabled: importJob.status === ImportJobStatus.ARCHIVED,
                        action: () => archiveImportJobHandler(),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.REPLACE(),
                        icon: FarseerIcons.Refresh,
                        disabled: importJob.status === ImportJobStatus.ARCHIVED,
                        action: () => replaceImportJob(),
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.DOWNLOAD_ORIGINAL_FILE(),
                        icon: FarseerIcons.Download,
                        disabled: importJob.source !== ImportJobSource.FARSEER_FILE || importJob.farseerFileId === null,
                        action: () =>
                            importJob.farseerFileId !== null
                                ? downloadFarseerFile(importJob.farseerFileId, importJob.title)
                                : {},
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.MISSING_MEMBERS_REPLACE_ALL(),
                        icon: FarseerIcons.Upload,
                        disabled: false,
                        action: () => replaceAllPreviousDimensions(),
                    }}
                />
                <ContextMenu.Divider />
                <ContextMenu.Item
                    item={{
                        label: LL.DELETE(),
                        icon: Delete,
                        action: () => deleteImportJobHandler(),
                    }}
                />
            </ImportActionsMenu>

            {
                {
                    [ImportJobStatus.CREATED]: activeImportJobButtons,
                    [ImportJobStatus.COMMITED]: undoJobButton,
                    [ImportJobStatus.ARCHIVED]: <></>,
                    [ImportJobStatus.REVERTED]: activeImportJobButtons,
                }[importJob.status]
            }
        </>
    );
};
